import { useAppDispatch, useAppSelector } from './state'
import {
  getPolicies as getPoliciesAPI,
  getPolicy as getPolicyAPI,
  requestPolicyAdvise as requestPolicyAdviseAPI,
  postRebuildValueMeter as postRebuildValueMeterAPI,
  postContentsInsuranceValue as postContentsInsuranceValueAPI,
  generateInsuranceCard as generateInsuranceCardAPI
} from '../api/policies'
import {
  setCombiCollectionAmount,
  setIsLoadingInsuranceCard,
  setIsPoliciesLoading,
  setPolicies,
  setPolicyDetails,
  setValueMeterIsLoading,
  updatePolicy
} from '../state/slices/policies_slice'
import { getResourceAuthorization } from '../state/selectors/resource_authorization'
import { getIVBConfig as getIVBConfigAPI } from '../api/ivb'
import { setIVBConfig } from '../state/slices/ivb_slice'
import { getPolicies as getPoliciesSelector } from '../state/selectors/policies'
import * as Sentry from '@sentry/react'

export const usePolicies = () => {
  const dispatch = useAppDispatch()
  const policies = useAppSelector(getPoliciesSelector)
  const currentResourceAuthorization = useAppSelector(getResourceAuthorization)

  const getPolicies = async (relationNumber?: number) => {
    const effectiveRelationNumber =
      relationNumber ?? currentResourceAuthorization?.relationNumber

    if (!effectiveRelationNumber) {
      return
    }

    dispatch(setIsPoliciesLoading(true))
    try {
      const result = await getPoliciesAPI(effectiveRelationNumber)

      if (result.policies) {
        dispatch(setPolicies(result.policies))
      }

      if (result.combiCollectionAmount !== undefined) {
        dispatch(setCombiCollectionAmount(result.combiCollectionAmount))
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setIsPoliciesLoading(false))
    }
  }

  const getPolicy = async (id: string) => {
    if (!currentResourceAuthorization?.relationNumber) {
      return
    }

    dispatch(setIsPoliciesLoading(true))
    try {
      const result = await getPolicyAPI(
        id,
        currentResourceAuthorization?.relationNumber
      )

      if (result) {
        dispatch(setPolicyDetails(result))
        dispatch(updatePolicy(result))
      }

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setIsPoliciesLoading(false))
    }
  }

  const updatePolicyOnPolicyNumber = async (policyNumber: string) => {
    if (!currentResourceAuthorization?.relationNumber) {
      return
    }

    const existingPolicy = policies.find(
      policy => policy.policyNumber === policyNumber
    )

    if (!existingPolicy) {
      Sentry.captureMessage(
        `Policy with number ${policyNumber} not found in state when trying to update policy details`
      )

      return
    }

    try {
      const result = await getPolicyAPI(
        existingPolicy.id,
        currentResourceAuthorization?.relationNumber
      )

      if (result) {
        dispatch(updatePolicy(result))
      }
    } catch (error) {
      console.error('Error updating policy on policy number:', error)
    }
  }

  const postRebuildValueMeter = async (
    policyId: string,
    data: Record<string, unknown>
  ) => {
    if (
      !currentResourceAuthorization?.relationNumber ||
      !currentResourceAuthorization?.relationInfo?.agentNumber
    ) {
      return
    }

    dispatch(setValueMeterIsLoading(true))
    try {
      const refKey = currentResourceAuthorization.relationInfo?.agentNumber

      await postRebuildValueMeterAPI(
        refKey,
        currentResourceAuthorization?.relationNumber,
        policyId,
        data
      )

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setValueMeterIsLoading(false))
    }
  }

  const postContentsInsuranceValue = async (
    id: string,
    data: Record<string, unknown>
  ) => {
    if (
      !currentResourceAuthorization?.relationNumber ||
      !currentResourceAuthorization?.relationInfo?.agentNumber
    ) {
      return
    }

    dispatch(setValueMeterIsLoading(true))
    try {
      const refKey = currentResourceAuthorization.relationInfo?.agentNumber

      await postContentsInsuranceValueAPI(
        refKey,
        currentResourceAuthorization?.relationNumber,
        id,
        data
      )

      dispatch(setValueMeterIsLoading(false))

      return true
    } catch (error) {
      return false
    } finally {
      dispatch(setValueMeterIsLoading(false))
    }
  }
  const requestPolicyAdvise = async (
    relationId: string,
    agencyId: string,
    adviseExplanation: string,
    products: string[]
  ) => {
    try {
      await requestPolicyAdviseAPI(
        relationId,
        agencyId,
        adviseExplanation,
        products
      )

      return true
    } catch (err) {
      return false
    }
  }

  const generateInsuranceCard = async (
    policyNumberPullingVehicle: string,
    policyNumberTowedVehicle: string
  ) => {
    dispatch(setIsLoadingInsuranceCard(true))
    try {
      const relationNumber = currentResourceAuthorization?.relationNumber
      const refKey = currentResourceAuthorization?.relationInfo?.agentNumber

      if (!relationNumber || !refKey) {
        dispatch(setIsLoadingInsuranceCard(false))

        return
      }

      const { data } = await generateInsuranceCardAPI(
        refKey,
        relationNumber,
        policyNumberPullingVehicle,
        policyNumberTowedVehicle
      )

      return data
    } catch (error) {
      return false
    } finally {
      dispatch(setIsLoadingInsuranceCard(false))
    }
  }

  const getIVBConfig = async () => {
    try {
      const result = await getIVBConfigAPI()

      dispatch(setIVBConfig(result.data))
    } catch (error) {
      console.error('Error fetching IVB config:', error)
    }
  }

  return {
    getPolicies,
    getPolicy,
    requestPolicyAdvise,
    postRebuildValueMeter,
    postContentsInsuranceValue,
    generateInsuranceCard,
    getIVBConfig,
    updatePolicyOnPolicyNumber
  }
}

export default usePolicies
